import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  FlaskConical, 
  Shield, 
  Clock, 
  MapPin, 
  FileText, 
  Activity, 
  ChevronRight,
  CheckCircle2,
  ArrowRight,
  Microscope,
  Heart,
  Brain,
  Droplets,
  Syringe,
  TestTubes
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Clock,
      title: 'Fast Results',
      description: 'Get your lab results within 24-48 hours with real-time tracking.'
    },
    {
      icon: MapPin,
      title: 'Home Collection',
      description: 'Book sample collection at your doorstep. No clinic visits needed.'
    },
    {
      icon: Shield,
      title: 'Secure & Private',
      description: 'Your medical data is encrypted and protected with HIPAA compliance.'
    },
    {
      icon: FileText,
      title: 'Digital Records',
      description: 'Access all your lab results and medical history in one place.'
    },
    {
      icon: Activity,
      title: 'AI Insights',
      description: 'Get AI-powered health insights and trend analysis.'
    },
    {
      icon: Microscope,
      title: 'Advanced Labs',
      description: 'Partner labs with state-of-the-art diagnostic equipment.'
    }
  ];

  const categories = [
    { icon: Droplets, name: 'Blood Tests', count: '45+ tests', color: 'bg-red-50 text-red-600' },
    { icon: Heart, name: 'Cardiac Panel', count: '12+ tests', color: 'bg-pink-50 text-pink-600' },
    { icon: Brain, name: 'Hormone Tests', count: '20+ tests', color: 'bg-purple-50 text-purple-600' },
    { icon: Syringe, name: 'Allergy Panel', count: '30+ tests', color: 'bg-amber-50 text-amber-600' },
    { icon: TestTubes, name: 'Urine Analysis', count: '15+ tests', color: 'bg-blue-50 text-blue-600' },
    { icon: FlaskConical, name: 'Full Body', count: '8 packages', color: 'bg-emerald-50 text-emerald-600' },
  ];

  const stats = [
    { value: '50K+', label: 'Tests Completed' },
    { value: '24hr', label: 'Avg. Turnaround' },
    { value: '99.9%', label: 'Accuracy Rate' },
    { value: '4.9', label: 'Patient Rating' },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Navbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="hero-pattern absolute inset-0 opacity-50" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <Badge className="mb-4 bg-sky-50 text-sky-700 border-sky-100 hover:bg-sky-100">
                Trusted Diagnostics
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight tracking-tight">
                Precise Insight{' '}
                <span className="text-sky-600">& Accuracy</span>
              </h1>
              <p className="mt-6 text-lg text-slate-600 max-w-lg">
                Book lab tests online, get home sample collection, and receive accurate 
                results with actionable health insights. Your trusted diagnostic partner.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Button 
                  size="lg" 
                  className="bg-sky-600 hover:bg-sky-700 text-white px-8 group"
                  onClick={() => navigate('/tests')}
                  data-testid="hero-browse-tests-btn"
                >
                  Browse Tests
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-slate-300"
                  onClick={() => navigate('/register')}
                  data-testid="hero-get-started-btn"
                >
                  Get Started Free
                </Button>
              </div>
              <div className="mt-10 flex items-center gap-6 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  <span>No hidden fees</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  <span>Quality assured</span>
                </div>
              </div>
            </div>
            <div className="relative lg:pl-8">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_9e3eb863-9f56-4b53-ae60-1c92c3c49a59/artifacts/dbqlxfu1_ae42544b-810b-406a-a82e-6757a760f0fe.JPG"
                  alt="Lab scientist using microscope at Factual Medical Diagnostic Centre"
                  className="w-full h-auto object-cover"
                  data-testid="hero-image"
                />
              </div>
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 animate-fade-in hidden sm:block">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Results Ready</p>
                    <p className="text-xs text-slate-500">Your CBC report is available</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-slate-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl lg:text-4xl font-bold text-white">{stat.value}</p>
                <p className="text-sm text-slate-400 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Test Categories */}
      <section className="py-20 bg-slate-50" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
              Comprehensive Test Categories
            </h2>
            <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
              From routine blood work to specialized diagnostics, find the tests you need.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.map((cat, index) => {
              const Icon = cat.icon;
              return (
                <button
                  key={index}
                  onClick={() => navigate('/tests')}
                  className="p-6 bg-white rounded-xl border border-slate-200 hover:border-sky-300 hover:shadow-md transition-all text-left group"
                  data-testid={`category-${cat.name.toLowerCase().replace(' ', '-')}`}
                >
                  <div className={`w-12 h-12 ${cat.color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-semibold text-slate-900">{cat.name}</h3>
                  <p className="text-sm text-slate-500 mt-1">{cat.count}</p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
              Why Choose Factual Medical?
            </h2>
            <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
              We combine cutting-edge technology with healthcare expertise to deliver 
              the best diagnostic experience.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index} 
                  className="card-clinical hover:shadow-md transition-shadow"
                  data-testid={`feature-${index}`}
                >
                  <div className="w-12 h-12 bg-sky-50 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-sky-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-slate-50" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
              How It Works
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Get your lab tests done in 4 simple steps
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Choose Tests', desc: 'Browse our catalog and select the tests you need.' },
              { step: '02', title: 'Book Appointment', desc: 'Pick a convenient date, time, and collection type.' },
              { step: '03', title: 'Sample Collection', desc: 'Visit a lab or get home collection service.' },
              { step: '04', title: 'Get Results', desc: 'Receive digital results with AI-powered insights.' },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-sky-600 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-white">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-sky-600" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            Ready to Take Control of Your Health?
          </h2>
          <p className="mt-4 text-lg text-sky-100 max-w-2xl mx-auto">
            Join thousands of patients who trust Factual Medical Diagnostic Centre for their diagnostic needs. 
            Start with a free account today.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Button 
              size="lg" 
              className="bg-white text-sky-600 hover:bg-slate-100 px-8"
              onClick={() => navigate('/register')}
              data-testid="cta-register-btn"
            >
              Create Free Account
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-sky-700"
              onClick={() => navigate('/tests')}
              data-testid="cta-browse-btn"
            >
              Browse Tests
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
